import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, Plus, Search, Trophy, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useVariantCompetitorVotes } from '@/hooks/useVariantCompetitorVotes';
import { cn } from '@/lib/utils';
import type { Tables } from '@/integrations/supabase/types';

interface Props {
  variantId: string;
  currentModel: Tables<'models'>;
  currentPrice: number | null;
}

function normalize(v: string) {
  return v
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa');
}

export function VariantCompetitorVote(props: Props) {
  const { data, isLoading, setVote, clearVote, isPending } = useVariantCompetitorVotes(props);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');

  const shownIds = new Set(data.candidates.map((c) => c.candidate.modelId));
  const searchResults = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return [];
    return data.allModels
      .filter((m) => normalize(`${m.brandName} ${m.modelName}`).includes(q))
      .slice(0, 8);
  }, [query, data.allModels]);

  const handleVote = (competitorModelId: string, alreadyVoted: boolean) => {
    if (alreadyVoted) {
      clearVote();
      return;
    }
    setVote(competitorModelId);
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-xl shadow-card p-5">
        <p className="text-sm text-muted-foreground">Indlæser konkurrenter…</p>
      </div>
    );
  }

  if (!data.candidates.length && !data.allModels.length) return null;

  const total = data.totalVotes;
  const sorted = data.candidates;

  return (
    <div className="bg-card rounded-xl shadow-card p-5 space-y-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-display font-semibold text-lg">Nærmeste konkurrent?</h3>
        <span className="text-xs text-muted-foreground">
          {total === 0 ? 'Ingen stemmer endnu' : `${total} ${total === 1 ? 'stemme' : 'stemmer'}`}
        </span>
      </div>

      <p className="text-xs text-muted-foreground">
        De 5 mest stemte modeller vises med flest stemmer øverst. Tryk for at stemme — tryk igen for at fjerne.
      </p>

      <div className="space-y-2">
        {sorted.map((row) => {
          const c = row.candidate;
          const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
          const isTop = data.topModelId === c.modelId && row.count > 0;
          return (
            <div
              key={c.modelId}
              className={cn(
                'group relative rounded-lg border overflow-hidden transition-colors',
                row.voted ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/40',
              )}
            >
              {/* Result bar */}
              <div
                className={cn(
                  'absolute inset-y-0 left-0 transition-all',
                  row.voted ? 'bg-accent/15' : 'bg-muted/60',
                )}
                style={{ width: `${pct}%` }}
                aria-hidden
              />

              <div className="relative flex items-center gap-3 p-2.5">
                <button
                  type="button"
                  onClick={() => handleVote(c.modelId, row.voted)}
                  disabled={isPending}
                  aria-pressed={row.voted}
                  className="flex items-center gap-3 flex-1 text-left"
                >
                  <div className="w-12 h-12 rounded-md bg-muted flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {c.imageUrl ? (
                      <img
                        src={c.imageUrl}
                        alt={`${c.brandName} ${c.modelName}`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Car className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground truncate">{c.brandName}</p>
                    <p className="font-medium text-sm truncate flex items-center gap-1.5">
                      {c.modelName}
                      {isTop && <Trophy className="w-3.5 h-3.5 text-accent" />}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold tabular-nums">{pct}%</p>
                    <p className="text-[10px] text-muted-foreground tabular-nums">{row.count}</p>
                  </div>
                </button>
                {row.voted && (
                  <button
                    type="button"
                    onClick={() => handleVote(c.modelId, true)}
                    disabled={isPending}
                    aria-label="Fjern stemme"
                    className="p-1 rounded hover:bg-muted text-muted-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <Link
                to={`/biler/${c.brandSlug}/${c.modelSlug}`}
                className="relative block px-2.5 pb-2 text-xs text-accent hover:underline"
              >
                Se model →
              </Link>
            </div>
          );
        })}
      </div>

      <div className="pt-1 border-t border-border">
        {!pickerOpen ? (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="w-full flex items-center justify-center gap-1.5 py-2 text-sm text-accent hover:underline"
          >
            <Plus className="w-4 h-4" />
            Vælg en anden bil
          </button>
        ) : (
          <div className="pt-3 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Søg mærke eller model…"
                className="pl-9"
              />
            </div>
            {query.trim().length > 0 && (
              <ul className="max-h-56 overflow-auto rounded-lg border border-border divide-y divide-border">
                {searchResults.length === 0 && (
                  <li className="px-3 py-2 text-sm text-muted-foreground">Ingen modeller fundet</li>
                )}
                {searchResults.map((m) => (
                  <li key={m.modelId}>
                    <button
                      type="button"
                      disabled={isPending}
                      onClick={() => {
                        setVote(m.modelId);
                        setQuery('');
                        setPickerOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 text-foreground flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        <span className="text-muted-foreground">{m.brandName}</span> {m.modelName}
                      </span>
                      {shownIds.has(m.modelId) && (
                        <span className="text-[10px] text-muted-foreground flex-shrink-0">på listen</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => {
                setPickerOpen(false);
                setQuery('');
              }}
              className="text-xs text-muted-foreground hover:underline"
            >
              Luk
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
